import React, { useMemo, useReducer, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { requestAccess, requestPlatformAccess } from '../../../services/api/request-access'
import { readAccessRequestRetryDate, rememberAccessRequest } from '../../../util/access-requests'
import { openChat } from '../../../util/set-crisp-session-info'
import { getProviderAccount } from '../../../util/remembered-login'
import { useAppDispatch } from '../../../store/hooks'
import { setAuthState } from '../../../store/auth-state-slice'
import { AuthStateEnum } from '@vacationtracker/shared/types/auth-state'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { FrontendUrls } from '../../../types/urls'
import { getDashboardUrl } from '../../../util/get-dashboard-url'
import { useAuthPageRecording } from '../../../hooks/use-auth-page-recording'

import SigninBannerView from './components/signin-banner'
import SigninLayout from './components/signin-layout'
import SigninModals from './components/signin-modals'
import SigninStepView, { ISigninStepHandlers } from './components/signin-step'
import { useEmailSignin } from './hooks/use-email-signin'
import { usePasswordReset } from './hooks/use-password-reset'
import { usePlatformSignin } from './hooks/use-platform-signin'
import { useSigninBootstrap, isAdpCallbackPopup } from './hooks/use-signin-bootstrap'
import { useSigninModals } from './hooks/use-signin-modals'
import { useSigninSession } from './hooks/use-signin-session'
import { useTransientBanner } from './hooks/use-transient-banner'
import { parseSigninQueryParams, searchWithoutPlatform } from './query-params'
import { buildInitialState, reducer } from './reducer'
import { resolveHandover, resolveInitialBanner, resolveInitialStep } from './initial-step'
import { bannerAfterAccessRequest, previousAccountBanner } from './outcomes'
import { setSigninEmail, trackSignin } from './analytics'
import { DetectedPlatform, INewSigninProps, SigninStep } from './types'

interface IStepCopy {
  titleId: string
  descriptionId: string
}

/** Steps that retitle the page. The rest keep the sign-in heading. */
const STEP_COPY: Partial<Record<SigninStep['name'], IStepCopy>> = {
  adp: { titleId: 'signin.adpLoginTitle', descriptionId: 'signin.adpLoginDescription' },
  mfa: { titleId: 'app.mfaEnterCode', descriptionId: 'app.mfaCodePlaceholder' },
  resetRequest: { titleId: 'app.resetPassword', descriptionId: 'signin.resetRequestDescription' },
  resetCode: { titleId: 'app.resetPassword', descriptionId: 'modal.forgotPassword.step2Intro' },
}

const DEFAULT_STEP_COPY: IStepCopy = {
  titleId: 'signin.goodToSeeYouAgain',
  descriptionId: 'signin.loginDescription',
}

interface IAccessAsk {
  /** Keys the "already asked" record in this browser. */
  email: string
  requestType: 'reactivate' | 'add'
  send: () => Promise<void>
}

const NewSignin = ({ location }: INewSigninProps): React.ReactElement => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  // Derived from the URL while rendering. Copying these into state from an effect is
  // what made the old page redirect twice and render once with the wrong target.
  const queryParams = useMemo(() => parseSigninQueryParams(location.search), [location.search])
  const redirectUrl = queryParams.redirect || getDashboardUrl()

  // Sign-up's answer wins over the URL and the remembered marker: it is the only one of the
  // three that knows what the visitor just did.
  const [state, localDispatch] = useReducer(reducer, { queryParams, handedOver: location.state }, ({ queryParams: params, handedOver }) => {
    const handover = resolveHandover(handedOver)
    return handover ? buildInitialState(handover.step, handover.banner) : buildInitialState(resolveInitialStep(params), resolveInitialBanner(params))
  })
  const stepEmail = 'email' in state.step ? (state.step.email ?? '') : ''
  // The previous-account hint is worked out, not stored, so closing it is remembered per address.
  const [previousAccountDismissedFor, setPreviousAccountDismissedFor] = useState<string | null>(null)
  const [isRequestingAccess, setIsRequestingAccess] = useState(false)
  const accessRequestInFlightRef = useRef(false)

  const modals = useSigninModals()
  const session = useSigninSession({ localDispatch, modals })
  const emailSignin = useEmailSignin({ step: state.step, localDispatch, session, modals })
  const passwordReset = usePasswordReset({ step: state.step, localDispatch })
  const platformSignin = usePlatformSignin({
    localDispatch,
    session,
    modals,
    // The platform flows learn the address after the fact; the analytics module stamps it on
    // every later event.
    onEmailResolved: setSigninEmail,
  })

  useTransientBanner({ banner: state.banner, localDispatch })
  useAuthPageRecording('signin')

  const isLeavingForApp = useSigninBootstrap({ queryParams, search: location.search, redirectUrl, initialEmail: stepEmail })

  /**
   * The address is carried over only when the lookup has just said it has no account, which is
   * the one case where sign-up wants it. Reached any other way it belongs to an account that
   * exists, so sign-up would open prefilled with an address it must immediately reject. A stale
   * value from an earlier visit is cleared for the same reason.
   */
  const goToSignup = (carryEmail = false) => {
    if (carryEmail && stepEmail) {
      sessionStorage.setItem('vtUserEmail', stepEmail)
    } else {
      sessionStorage.removeItem('vtUserEmail')
    }
    localDispatch({ type: 'busyChanged', isBusy: false })
    dispatch(setAuthState(AuthStateEnum.signUp))
    history.push(FrontendUrls.signup)
  }

  /**
   * The one thing a visitor who cannot get in can do from here. The answer never says whether an
   * account was found, so a failure has to be a real one (a request that never left the
   * browser) rather than the endpoint declining to act.
   */
  const askForAccess = async ({ email, requestType, send }: IAccessAsk) => {
    // The ref, not the state: a second click can land before the disabled link has rendered.
    if (accessRequestInFlightRef.current) return
    trackSignin('SIGN_IN_REQUEST_ACCESS_CLICKED', { email, fromStep: state.step.name, requestType })

    // The endpoint drops a repeat inside its cooldown and answers as if it had sent one, so
    // there is nothing to ask for: the page answers from what this browser already sent. The
    // record lives in `vtAccessRequests`, which is what to clear when testing the endpoint.
    const retryOn = readAccessRequestRetryDate(email)
    if (retryOn) {
      trackSignin('SIGN_IN_REQUEST_ACCESS_SKIPPED', { email, requestType })
      localDispatch({
        type: 'bannerRaised',
        banner: { kind: 'accessAlreadyRequested', retryOn: retryOn.toISOString() },
      })
      return
    }

    accessRequestInFlightRef.current = true
    setIsRequestingAccess(true)
    try {
      await send()
      rememberAccessRequest(email)
      localDispatch({ type: 'bannerRaised', banner: { kind: 'accessRequested' } })
    } catch (error) {
      trackSignin('SIGN_IN_REQUEST_ACCESS_FAILED', { email, requestType })
      localDispatch({ type: 'bannerRaised', banner: { kind: 'accessRequestFailed' } })
    } finally {
      accessRequestInFlightRef.current = false
      setIsRequestingAccess(false)
    }
  }

  // A deactivated account is found by its address, which is all the email path needs.
  const askToBeReactivated = (email: string) => void askForAccess({ email, requestType: 'reactivate', send: () => requestAccess(email) })

  // Someone never added has no row for the address to find, so the ask goes with the token of the
  // sign-in that just ended here, and the endpoint reaches the company that sign-in found.
  const askToBeAdded = () => {
    if (state.step.name !== 'noAccess') return
    const { email, platform } = state.step
    const token = platformSignin.readProviderToken(platform)
    const send = () => (token ? requestPlatformAccess({ platform, token }) : Promise.reject(new Error('No provider sign-in to send')))
    void askForAccess({ email, requestType: 'add', send })
  }

  const handlers: ISigninStepHandlers = {
    onSubmitEmail: emailSignin.submitEmail,
    onSubmitPassword: emailSignin.submitPassword,
    onSubmitAdp: platformSignin.connectWithAdp,
    onSubmitMfaCode: emailSignin.enterMfaCode,
    onSigninWithEmail: () => localDispatch({ type: 'emailSigninChosen' }),
    // Only the MFA step has a half-finished sign-in to end; the reset steps are just leaving.
    onBackToSignin: () => {
      if (state.step.name === 'mfa') {
        emailSignin.cancelMfa()
        return
      }
      localDispatch({ type: 'restarted' })
    },
    onPlatformSignin: (platform: DetectedPlatform) => platformSignin.signinWith(platform, stepEmail),
    onEmailChanged: emailSignin.emailChanged,
    onBannerDismissed: () => localDispatch({ type: 'bannerCleared' }),
    onChangeEmail: () => {
      trackSignin('SIGN_IN_CHANGE_EMAIL_CLICKED', { fromStep: state.step.name, action: 'change' })
      setSigninEmail(undefined)
      localDispatch({ type: 'restarted' })
    },
    // A link naming one platform is an entry point, not a lock-in. The param goes with the
    // step, so a refresh does not put the visitor back on the button they just turned down.
    onDifferentMethod: (platform: DetectedPlatform) => {
      trackSignin('SIGN_IN_DIFFERENT_METHOD_CLICKED', { platform })
      localDispatch({ type: 'restarted' })
      history.replace(FrontendUrls.signin + searchWithoutPlatform(location.search))
    },
    // The link only opens the reset; sending the code is a separate press. Sending it straight
    // from here would mean a mis-click puts an email in someone's inbox with nothing asked.
    // The address carries over when the page knows it, so the extra press is just a press.
    onForgotPassword: () => {
      trackSignin('SIGN_IN_PASSWORD_RESET_STARTED', { fromStep: state.step.name })
      localDispatch({ type: 'resetStarted', email: stepEmail })
    },
    onRequestResetCode: passwordReset.requestCode,
    onSubmitNewPassword: passwordReset.submitNewPassword,
    onResendResetCode: passwordReset.resendCode,
    // The footer link is a plain "I want to sign up", so it starts from a blank field.
    onSignUp: () => goToSignup(),
  }

  // This tab is the ADP callback popup, not the page: the bootstrap hook hands the code
  // to the opener and closes the window, so there is nothing to draw.
  if (isAdpCallbackPopup(queryParams)) {
    return <></>
  }

  // A live session has nothing to sign in to. The redirect runs from an effect, which is after
  // paint, so without this the form is drawn for a frame before it is navigated away.
  if (isLeavingForApp) {
    return <></>
  }

  const copy = STEP_COPY[state.step.name] ?? DEFAULT_STEP_COPY

  // A visitor who asked before a refresh is told so on arrival, rather than being offered an
  // ask the endpoint would drop.
  // Any outcome of an attempt outranks the hint about who the next one will sign in.
  const previousAccount = previousAccountBanner({ step: state.step, previousSlackAccount: getProviderAccount(PlatformEnum.Slack) })
  const shownBanner =
    state.banner.kind === 'inactiveAccount' || state.banner.kind === 'accessNeeded' || state.banner.kind === 'noAccess'
      ? bannerAfterAccessRequest(state.banner, readAccessRequestRetryDate(state.banner.email))
      : state.banner.kind === 'none' && previousAccount && previousAccount.email !== previousAccountDismissedFor
        ? previousAccount
        : state.banner

  return (
    <>
      <SigninLayout
        titleId={copy.titleId}
        descriptionId={copy.descriptionId}
        isBusy={state.isBusy}
        banner={
          <SigninBannerView
            banner={shownBanner}
            onSignUp={() => goToSignup(true)}
            onForgotPassword={handlers.onForgotPassword}
            onDismiss={shownBanner.kind === 'previousPlatformAccount' ? () => setPreviousAccountDismissedFor(shownBanner.email) : handlers.onBannerDismissed}
            onRequestAccess={askToBeReactivated}
            onRequestPlatformAccess={askToBeAdded}
            isRequestingAccess={isRequestingAccess}
            onContactSupport={() => openChat()}
          />
        }
      >
        <SigninStepView step={state.step} isBusy={state.isBusy} handlers={handlers} />
      </SigninLayout>

      <SigninModals
        modals={modals}
        onRetrySlackWithFullScopes={platformSignin.retrySlackWithFullScopes}
        onSaveNewPassword={emailSignin.saveNewPassword}
        onCancelNewPassword={emailSignin.cancelNewPassword}
      />
    </>
  )
}

export default NewSignin
